import { useDndContext, useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Tooltip } from '@mui/material';
import { Metric } from '@strength/domain';
import { PrimitiveAtom, useAtom } from 'jotai';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getBenchmarkBadgeGridName,
  hasPercentageIntensity,
  useBenchmarks,
} from '../../../hooks/useBenchmarks';
import { useMetrics } from '../../../hooks/useMetrics';
import { WorkoutColumnInfo } from '../../../pages/block/slots/workout';
import { jotaiStore } from '../../../utils/jotai';
import { BenchmarkBadge } from '../../badge/BenchmarkBadge';
import { cn } from '../../utils';
import {
  Cell,
  CellEditBehavior,
  Region,
  Slot,
  SlotRenderContentProps,
  SlotRenderContentResult,
} from '../types';
import { relativeCell } from '../utils';
import { CellInputWithAtom, MetricCellAutocomplete } from './common';

type SlotMetric<MetricType extends string> = {
  metricId: string;
  metricName: string;
  metricType: MetricType;
  workoutExerciseMetricId?: string;
  benchmarkGridId?: string;
};

type MetricValue = {
  metricId: string;
  input: string;
};

type RawDataFn<
  T,
  M extends SlotMetric<MetricType>,
  MetricType extends string,
  RawData,
> = (rows: T[], rowMetrics: M[], headerMetrics: M[]) => RawData[];

type Row = {
  metrics: readonly MetricValue[];
};

type SpecsSlotOptions<
  T extends Row,
  M extends SlotMetric<MetricType>,
  MetricType extends string,
  RawData,
> = {
  hideHeaders?: boolean;
  rawDataFn?: RawDataFn<T, M, MetricType, RawData>;
  libraryExerciseId?: string;
  /**
   * True when this sheet collects actuals rather than prescriptions, which
   * drops the prescription-only `%` from cell unit suggestions. Distinct from
   * the `showActuals` constructor arg: that one makes the block builder, which
   * is a prescription sheet, display recorded actuals alongside the
   * prescription.
   */
  forActuals?: boolean;
};

export type RenderCellContentProps<
  T,
  M extends SlotMetric<MetricType>,
  MetricType extends string,
  Context,
> = {
  context: Context;
  metric: M;
  row: T;
  hideCollectionFlag?: boolean;
  disabled: boolean;
  badge?: React.ReactNode;
  originalSet?: T;
};

export class MetricsSlot<
  T extends Row,
  M extends SlotMetric<MetricType>,
  MetricType extends string,
  Context,
  RawData = undefined,
> implements Slot<RawData> {
  public readonly region: Region;
  public readonly disabled: boolean;
  private readonly gridSubRowSpan: number;
  private readonly workoutColumnInfo?: {
    setsColumnCount: number;
    nonSetsColumnCount: number;
    workoutStartColumn: number; // Cumulative column start position for this workout
    isReadOnly?: boolean;
    isAthleteSheet?: boolean;
    shouldShowSetsColumn?: boolean;
  };

  constructor(
    public readonly isReadOnly: boolean,
    public readonly isDisabledMetric: (metric: M) => boolean,
    private readonly anchorCell: Cell,
    private readonly metrics: readonly M[],
    private readonly rows: readonly T[],
    private readonly onChange: (edit: TEdit, row: T) => void,
    private readonly activeInputAtom: PrimitiveAtom<string>,
    private readonly renderCellContent: (
      props: RenderCellContentProps<T, M, MetricType, Context>,
    ) => React.ReactNode,
    private readonly context: Context,
    private readonly gridSubRows: number,
    hiddenColumnsCount: number,
    private readonly getMetric: (metricId: string) => Metric,
    private readonly options?: SpecsSlotOptions<T, M, MetricType, RawData>,
    private readonly className?: string,
    private readonly dragContext?: {
      workoutExerciseId: string;
      workoutSectionId: string;
      workoutId: string;
    },
    workoutColumnInfo?: WorkoutColumnInfo,
    private readonly showActuals?: boolean,
  ) {
    // Not sure the empty column case is handled correctly
    this.region = {
      anchor: this.anchorCell,
      height: this.rows.length + (this.options?.hideHeaders ? 0 : 1),
      width: this.metrics.length + hiddenColumnsCount,
      isMerged: false,
      // Merge the final column of cells with implicitly "hidden" cells in the row
      // This (combined with grid overrides below) is done in order to support different number of cells per row
      // The span must match the region's row count exactly: when headers are
      // hidden the region is one row shorter, and the extra subregion would
      // bleed into the next slot's row and steal its cell in buildCellLookup
      // (Bulk Actuals renders one slot per set — every set but the first went
      // blank).
      mergedSubRegions: Array.from({
        length: this.rows.length + (this.options?.hideHeaders ? 0 : 1),
      }).map((_, j) => ({
        anchor: {
          x: this.anchorCell.x + this.metrics.length - 1,
          y: this.anchorCell.y + j,
        },
        height: 1,
        width: hiddenColumnsCount + 1,
        isMerged: true,
      })),
    };

    this.gridSubRows = gridSubRows;
    this.gridSubRowSpan = (this.region.width * gridSubRows) / metrics.length;
    this.disabled = isReadOnly;
    this.isDisabledMetric = isDisabledMetric;
    this.className = className;
    this.workoutColumnInfo = workoutColumnInfo;
  }

  public isDisabledCell(cell: Cell): boolean {
    // Disable if the slot is read-only else
    // check if the metric is disabled
    if (this.disabled) {
      return true;
    }
    const relative = relativeCell(cell, this.region);
    const metric = this.getSlotMetric(relative);
    return metric ? this.isDisabledMetric(metric) : false;
  }

  private getSlotMetric(relativeCell: Cell): M | undefined {
    return this.metrics[relativeCell.x];
  }

  private getMetricValue(relativeCell: Cell): MetricValue | undefined {
    const metric = this.getSlotMetric(relativeCell);
    const row = this.getRow(relativeCell.y);
    return row?.metrics.find((m) => m.metricId === metric?.metricId);
  }

  public renderContent({
    cell,
    editing,
    onBlur,
  }: SlotRenderContentProps): SlotRenderContentResult {
    const relative = relativeCell(cell, this.region);

    // Use new positioning logic if workoutColumnInfo is provided
    // Start with default positioning
    const style: React.CSSProperties = {
      gridColumnStart:
        relative.x * this.gridSubRowSpan +
        1 +
        this.anchorCell.x * this.gridSubRows,
      gridRowStart: cell.y + 1,
      gridColumnEnd: `span ${this.gridSubRowSpan}`,
    };

    // Override with workout-aware positioning if available
    if (this.workoutColumnInfo) {
      const metric = this.getSlotMetric(relative);
      if (metric) {
        const isSetsMetric = metric.metricType === 'sets';

        if (isSetsMetric) {
          // Sets: Fixed 40px column, only show if this workout should show sets
          if (this.workoutColumnInfo.shouldShowSetsColumn) {
            style.gridColumnStart =
              1 + this.workoutColumnInfo.workoutStartColumn;
            style.gridColumnEnd = `span 1`;
          } else {
            // If this workout shouldn't show sets, hide the sets metric
            return {
              content: null,
              style: { display: 'none' },
              overrideGridStyles: true,
            };
          }
        } else {
          // Non-Sets: Use LCM-based span calculation
          const nonSetsMetrics = this.metrics.filter(
            (m) => m.metricType !== 'sets',
          );
          const nonSetsMetricsCount = nonSetsMetrics.length;
          const nonSetsIndex = nonSetsMetrics.findIndex(
            (m) => m.metricId === metric.metricId,
          );

          if (nonSetsMetricsCount > 0 && nonSetsIndex >= 0) {
            const columnSpan =
              this.workoutColumnInfo.nonSetsColumnCount / nonSetsMetricsCount;
            // Use shouldShowSetsColumn to determine if this workout has a sets column
            const setsOffset = this.workoutColumnInfo.shouldShowSetsColumn
              ? 1
              : 0;

            style.gridColumnStart =
              1 + // CSS grid is 1-indexed
              this.workoutColumnInfo.workoutStartColumn +
              setsOffset +
              nonSetsIndex * columnSpan;

            style.gridColumnEnd = `span ${columnSpan}`;
          }
        }
      }
    }

    const metric = this.getSlotMetric(relative);
    if (!metric) {
      return { content: null, style, overrideGridStyles: true };
    }

    if (!this.options?.hideHeaders && relative.y === 0) {
      const percentageIntensity = hasPercentageIntensity(
        metric,
        this.rows.flatMap((row) => row.metrics),
      );

      return {
        content: (
          <MetricHeaderCell
            metric={metric}
            disabled={this.disabled}
            className={this.className}
            dragContext={this.dragContext}
            libraryExerciseId={this.options?.libraryExerciseId}
            hasPercentageIntensity={percentageIntensity}
          />
        ),
        style,
        overrideGridStyles: true,
      };
    }

    const row = this.getRow(relative.y);
    if (!row) {
      return {
        content: null,
        style,
        overrideGridStyles: true,
      };
    }

    const disabled = this.isDisabledCell(cell);
    return {
      content: (
        <CellContent
          disabled={disabled}
          row={row}
          context={this.context}
          inputAtom={this.activeInputAtom}
          metric={metric}
          editing={editing}
          onBlur={onBlur}
          renderCellContent={this.renderCellContent}
          showActuals={this.showActuals}
          forActuals={this.options?.forActuals}
        />
      ),
      style,
      className: this.className,
      overrideGridStyles: true,
    };
  }

  public getEditBehavior(cell: Cell): CellEditBehavior {
    if (this.disabled) {
      return { type: 'skip' };
    }

    const relative = relativeCell(cell, this.region);

    if (!this.options?.hideHeaders && relative.y === 0) {
      return {
        type: 'skip',
      };
    }

    return {
      type: 'input',
      onStart: (initInput) => {
        const metricValue = this.getMetricValue(relative);
        let input: string;
        if (initInput !== undefined) {
          input = initInput;
        } else if (metricValue) {
          input = metricValue.input;
        } else {
          input = '';
        }
        jotaiStore.set(this.activeInputAtom, input);
      },
      onCommit: () => {
        const metric = this.getSlotMetric(relative);
        const row = this.getRow(relative.y);
        if (!metric || !row) {
          return;
        }

        const metricConfig = this.getMetric(metric.metricId);
        const input = jotaiStore.get(this.activeInputAtom);
        let sanitizedInput = input;
        if (!metricConfig.config.keywords.includes(input)) {
          // Only strip the whitespace if this value isn't a valid keyword
          sanitizedInput = input.replace(/\s/g, '');
        }
        this.onChange(
          {
            metricId: metric.metricId,
            input: sanitizedInput,
          },
          row,
        );
        jotaiStore.set(this.activeInputAtom, '');
      },
    };
  }

  public getRawData(cells: readonly Cell[]): readonly RawData[] {
    const rawDataFn = this.options?.rawDataFn;
    if (!rawDataFn) {
      return [];
    }

    const rows = new Set<T>();
    const rowMetrics = new Set<M>();
    const headerMetrics = new Set<M>();

    for (const cell of cells) {
      const relative = relativeCell(cell, this.region);
      const metric = this.getSlotMetric(relative);
      if (!metric) {
        continue;
      }

      const row = this.getRow(relative.y);
      if (row) {
        rowMetrics.add(metric);
        rows.add(row);
      } else {
        headerMetrics.add(metric);
      }
    }

    return rawDataFn(
      Array.from(rows.values()),
      Array.from(rowMetrics.values()),
      Array.from(headerMetrics.values()),
    );
  }

  private getRow(relativeY: number) {
    // account for header row if using headers
    return this.rows[relativeY - (this.options?.hideHeaders ? 0 : 1)];
  }
}

interface CellContentProps<
  T,
  M extends SlotMetric<MetricType>,
  MetricType extends string,
  Context,
> {
  disabled: boolean;
  editing: boolean;
  onBlur: () => void;
  metric: M;
  row: T;
  inputAtom: PrimitiveAtom<string>;
  context: Context;
  showActuals?: boolean;
  forActuals?: boolean;
  renderCellContent: (
    props: RenderCellContentProps<T, M, MetricType, Context>,
  ) => React.ReactNode;
}

function CellContent<
  T extends Row,
  M extends SlotMetric<MetricType>,
  MetricType extends string,
  Context,
>({
  disabled,
  row,
  inputAtom,
  metric,
  context,
  editing,
  onBlur,
  renderCellContent,
  showActuals,
  forActuals,
}: CellContentProps<T, M, MetricType, Context>) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [displayedText, setDisplayedText] = useState('');

  const metricValue = row.metrics.find(
    (m: MetricValue) => m.metricId === metric.metricId,
  );
  // We get this just to check if the value has changed to limit how often we run the
  // useLayoutEffect below
  const metricValueInput = metricValue?.input || '';

  // Check if content is overflowing so we only show a tooltip when needed
  useLayoutEffect(() => {
    if (contentRef.current && contentRef.current.firstElementChild) {
      const element = contentRef.current.firstElementChild as HTMLElement;
      const isOverflow = element.scrollWidth > element.clientWidth;
      const text = element.innerText ?? '';
      setIsOverflowing(isOverflow);
      setDisplayedText(text);
    }
  }, [metricValueInput, showActuals]);

  // The input atom subscription lives in MetricCellEditing (SC-5564): keeping
  // useAtom here made every metric cell re-render on each keystroke.
  if (editing && !disabled) {
    return (
      <MetricCellEditing
        inputAtom={inputAtom}
        metric={metric}
        onBlur={onBlur}
        forActuals={forActuals}
      />
    );
  }

  const content = renderCellContent({
    context,
    metric,
    row,
    disabled,
  });

  return (
    <Tooltip
      title={displayedText}
      placement="top"
      arrow
      disableInteractive
      disableHoverListener={!displayedText || !isOverflowing}
    >
      <div ref={contentRef} className="w-full h-full">
        {content}
      </div>
    </Tooltip>
  );
}

/**
 * Editing-only branch of CellContent. Subscribing to the input atom here —
 * and only here — means a keystroke re-renders the one edited cell instead
 * of every rendered metric cell (SC-5564).
 */
function MetricCellEditing<MetricType extends string>({
  inputAtom,
  metric,
  onBlur,
  forActuals,
}: {
  inputAtom: PrimitiveAtom<string>;
  metric: SlotMetric<MetricType>;
  onBlur: () => void;
  forActuals?: boolean;
}) {
  const [input, setInput] = useAtom(inputAtom);
  const { getMetric, getUnitsForMetric } = useMetrics();
  const {
    config: { keywords },
  } = getMetric(metric.metricId);

  const inputWithoutUnits = input.replace(
    /(?!\d+(\.\d+)?-\d+(\.\d+)?)[^\d.-]+/g,
    '',
  );

  // if the metric is an intensity, show the default weight unit first
  const metricUnits = useMemo(() => {
    return getUnitsForMetric(metric.metricId, forActuals);
  }, [metric.metricId, getUnitsForMetric, forActuals]);
  // Don't show unit options if there isn't a value
  const options = [
    ...(inputWithoutUnits.length
      ? metricUnits.map((unit) => `${inputWithoutUnits}${unit}`)
      : []),
    ...keywords,
  ];

  if (options.length > 0) {
    return (
      <MetricCellAutocomplete
        options={options}
        value={input}
        setInput={setInput}
        onBlur={onBlur}
      />
    );
  }
  return <CellInputWithAtom inputAtom={inputAtom} onBlur={onBlur} />;
}

export type CellDisplayProps<MetricType extends string, Context> = {
  metric: SlotMetric<MetricType>;
  context: Context;
};

export type TEdit = {
  metricId: string;
  input: string;
};

function MetricHeaderCell<MetricType extends string>({
  metric,
  disabled,
  className,
  dragContext,
  libraryExerciseId,
  hasPercentageIntensity,
}: {
  metric: SlotMetric<MetricType>;
  disabled: boolean;
  className?: string;
  dragContext?: {
    workoutExerciseId: string;
    workoutSectionId: string;
    workoutId: string;
  };
  libraryExerciseId?: string;
  hasPercentageIntensity?: boolean;
}) {
  const { getBenchmarkGrid, defaultBenchmarkGridId } = useBenchmarks();
  const { active } = useDndContext();
  const canDrag =
    !disabled &&
    metric.workoutExerciseMetricId &&
    dragContext &&
    metric.metricType !== 'sets'; // Sets metric cannot be dragged

  const benchmarkGridName = getBenchmarkBadgeGridName(
    metric,
    getBenchmarkGrid,
    defaultBenchmarkGridId,
    { libraryExerciseId, hasPercentageIntensity },
  );

  const {
    attributes,
    listeners,
    setNodeRef: setDragNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: metric.workoutExerciseMetricId || `metric-${metric.metricId}`,
    disabled: !canDrag,
    data: {
      type: 'WorkoutExerciseMetric',
      workoutExerciseMetricId: metric.workoutExerciseMetricId,
      workoutExerciseId: dragContext?.workoutExerciseId,
      workoutSectionId: dragContext?.workoutSectionId,
      workoutId: dragContext?.workoutId,
      benchmarkGridId: metric.benchmarkGridId,
    },
  });

  const canDrop =
    active?.data.current?.['type'] === 'WorkoutExerciseMetric' &&
    active.data.current?.['workoutExerciseId'] ===
      dragContext?.workoutExerciseId &&
    metric.workoutExerciseMetricId &&
    dragContext;

  const { setNodeRef: setDropNodeRef, isOver } = useDroppable({
    id: `drop-${metric.workoutExerciseMetricId || metric.metricId}`,
    disabled: !canDrop,
    data: {
      type: 'WorkoutExerciseMetric',
      workoutExerciseMetricId: metric.workoutExerciseMetricId,
      workoutExerciseId: dragContext?.workoutExerciseId,
      workoutSectionId: dragContext?.workoutSectionId,
      workoutId: dragContext?.workoutId,
      benchmarkGridId: metric.benchmarkGridId,
    },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  const isDropHighlight = isOver && canDrop && !isDragging;

  return (
    <div
      ref={canDrop ? setDropNodeRef : undefined}
      className={cn(
        'bg-gray-100 text-slate-600 text-[11px] font-medium flex items-center  leading-tight h-full w-full',
        className,
        isDropHighlight && 'bg-blue-100 border-2 border-blue-300',
        isDragging && 'opacity-50',
        metric.metricType !== 'sets' && 'pl-2 py-2 truncate',
      )}
    >
      <div
        ref={canDrag ? setDragNodeRef : undefined}
        style={style}
        className={cn(
          metric.metricType === 'sets' && 'justify-center',
          'flex items-center w-full h-full',
        )}
        {...(canDrag ? attributes : {})}
        {...(canDrag ? listeners : {})}
      >
        <span className={cn(metric.metricType === 'sets' && 'font-semibold')}>
          {metric.metricName}
        </span>
        {benchmarkGridName && (
          <BenchmarkBadge benchmarkGridName={benchmarkGridName} />
        )}
      </div>
    </div>
  );
}
